import '../MaterialOperationModal.scss';

import { Button, FlexRow, Modal, SearchBox } from 'components';
import { handleApiErrorWithNotification } from 'helpers/errorHandling';
import { useCustomTranslation } from 'helpers/translationHelper';
import type { MaterialAtStore, SelectedMaterialItem } from 'pages/material/models';
import {
  useMoveMaterialToStoreMutation,
  useRemoveMaterialFromStoreMutation
} from 'pages/material/queries';
import React, { useState } from 'react';

import type { EditAmountFormValues, MaterialToEditAmount } from '../EditAmountForm';
import EditAmountForm from '../EditAmountForm';
import MaterialToRemoveList from './MaterialToRemoveList';
import StoreMaterialList from './StoreMaterialList';

interface Props {
  open: boolean;
  onClose: () => void;
  selectedStoreId: string;
  storeName: string;
}

export const REMOVE_FROM_STORE_ID = 'removeFromStoreId';

const MoveMaterialFromStoreModal: React.FC<Props> = (props): React.ReactElement => {
  const { selectedStoreId, onClose, open, storeName } = props;

  const { t } = useCustomTranslation();

  const moveMaterialToStoreMutation = useMoveMaterialToStoreMutation();
  const removeMaterialFromStoreMutation = useRemoveMaterialFromStoreMutation();

  const [searchPhrase, setSearchPhrase] = useState<string | undefined>(undefined);
  const [selectedMaterial, setSelectedMaterial] = useState<SelectedMaterialItem[]>([]);
  const [destinationStoreId, setDestinationStoreId] = useState(REMOVE_FROM_STORE_ID);
  const [itemToEdit, setItemToEdit] = useState<MaterialToEditAmount | undefined>();
  const [isMoving, setIsMoving] = useState(false);

  const removeMaterialFromSelected = (materialId: string) => {
    setSelectedMaterial(selectedMaterial.filter((x) => x.materialId !== materialId));
  };

  const setMaterialAmount = (material: MaterialAtStore, amount?: number | null) => {
    const selectedMaterialItem = selectedMaterial.find((x) => x.materialId === material.materialId);

    if (selectedMaterialItem && amount !== 0) {
      setSelectedMaterial(
        selectedMaterial.map((x) => {
          if (x.materialId === selectedMaterialItem.materialId) {
            return {
              name: selectedMaterialItem.name,
              materialId: x.materialId,
              unit: x.unit,
              maxAmount: material.amount,
              amount
            };
          }
          return x;
        })
      );
    } else if (selectedMaterialItem) {
      removeMaterialFromSelected(material.materialId);
    } else {
      const materialToStore: SelectedMaterialItem = {
        name: material.name,
        unit: material.materialUnit,
        maxAmount: material.amount,
        amount,
        materialId: material.materialId
      };

      setSelectedMaterial([...selectedMaterial, materialToStore]);
    }
  };

  const handleEditAmount = (values: EditAmountFormValues) => {
    if (values.amount === 0) {
      removeMaterialFromSelected(itemToEdit?.materialId || '');
    } else {
      setSelectedMaterial(
        selectedMaterial.map((material) => {
          if (material.materialId === itemToEdit?.materialId) {
            return {
              ...material,
              amount: values.amount
            };
          }

          return material;
        })
      );
    }

    setItemToEdit(undefined);
  };

  const handleClose = () => {
    if (itemToEdit) {
      setItemToEdit(undefined);
    } else {
      onClose();
    }
  };

  const handleMove = async () => {
    setIsMoving(true);
    try {
      if (destinationStoreId === REMOVE_FROM_STORE_ID) {
        await removeMaterialFromStoreMutation.mutateAsync({
          storeId: selectedStoreId,
          data: selectedMaterial
        });
      } else {
        await moveMaterialToStoreMutation.mutateAsync({
          storeId: selectedStoreId,
          moveToId: destinationStoreId,
          data: selectedMaterial
        });
      }
    } catch (e) {
      handleApiErrorWithNotification(e);
    } finally {
      setIsMoving(false);
      onClose();
    }
  };

  const getTitle = () => {
    if (itemToEdit) {
      return t('stores.editMaterialAmountTitle');
    }

    return t('stores.outcomeMaterialTitle', {
      value: storeName
    });
  };

  return (
    <Modal
      width={900}
      title={getTitle()}
      actions={<SearchBox onValueChanged={setSearchPhrase} />}
      onClose={handleClose}
      open={open}
      footer={
        !itemToEdit && (
          <FlexRow noMargin>
            <Button onClick={() => onClose()}>{t('common.cancel')}</Button>
            <Button onClick={() => handleMove()} type="primary" loading={isMoving}>
              {t('common.save')}
            </Button>
          </FlexRow>
        )
      }
      className="MaterialOperationModal"
      surviveClose={false}
    >
      {itemToEdit ? (
        <EditAmountForm
          item={itemToEdit}
          onCancel={() => setItemToEdit(undefined)}
          onSubmit={handleEditAmount}
          allText={t('stores.allAmountLabel')}
        />
      ) : (
        <div className="MaterialOperationModal_content">
          <StoreMaterialList
            selectedStoreId={selectedStoreId}
            searchPhrase={searchPhrase}
            selectedMaterial={selectedMaterial}
            setMaterialAmount={setMaterialAmount}
          />
          <MaterialToRemoveList
            items={selectedMaterial}
            destinationStoreId={destinationStoreId}
            setDestinationStoreId={setDestinationStoreId}
            onDelete={removeMaterialFromSelected}
            onEdit={setItemToEdit}
            selectedStoreId={selectedStoreId}
          />
        </div>
      )}
    </Modal>
  );
};

export default MoveMaterialFromStoreModal;
